import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {infiniteQueryOptions, QueryClient, useInfiniteQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {parseLinkHeader} from '../../utils/parse-link-header'
import type {
  ConversationComment,
  ConversationCommentsPayload,
  ConversationIssueComment,
  ConversationReviewSummary,
} from '../payloads/conversation-comments'
import {produce, type WritableDraft} from 'immer'

export interface ConversationCommentsPage {
  data: ConversationCommentsPayload['items']
  nextUrl: string | null
}

export function conversationCommentsApiUrl(basePath: string): string {
  return `${basePath}/page_data/${PageData.conversationComments}`
}

interface UseInfiniteConversationCommentsQueryOpts {
  basePath: string
  enabled?: boolean
}

function conversationCommentsQueryOptions(basePath: string) {
  return infiniteQueryOptions({
    queryKey: [PageData.conversationComments, basePath],
    initialPageParam: conversationCommentsApiUrl(basePath),
    queryFn: async ({pageParam}): Promise<ConversationCommentsPage> => {
      const result = await reactFetchJSON(pageParam)
      if (!result.ok) throw new Error(`Failed to fetch conversation comments from ${pageParam}: HTTP ${result.status}`)

      const json = (await result.json()) as ConversationCommentsPayload
      const linkHeader = result.headers.get('Link')
      const nextUrl = parseLinkHeader(linkHeader, 'next')
      const data = json.items

      return {data, nextUrl}
    },
    getNextPageParam: lastPage => lastPage.nextUrl,
    select: ({pages}) => pages.flatMap(page => page.data),
  })
}

export function conversationCommentsQueryKey(basePath: string) {
  return conversationCommentsQueryOptions(basePath).queryKey
}

export function useInfiniteConversationCommentsQuery({basePath, enabled}: UseInfiniteConversationCommentsQueryOpts) {
  return useInfiniteQuery({
    ...conversationCommentsQueryOptions(basePath),
    enabled,
  })
}

/**
 * Helper to a single issue comment in the conversation comments query store.
 * @param updateComment A function to update the comment. The passed item is a **mutable** (`immer`) draft;
 * you should edit this and return void.
 */
export function setCommentQueryData(
  client: QueryClient,
  basePath: string,
  commentId: number,
  updateComment: (draft: WritableDraft<ConversationIssueComment>) => void,
) {
  client.setQueryData(
    conversationCommentsQueryKey(basePath),
    data =>
      data &&
      produce(data, draftData => {
        for (const page of draftData.pages)
          for (const comment of page.data)
            // IDs are not unique across types, so it's important to also check `type`
            if (comment.type === 'issue' && comment.id === commentId) {
              updateComment(comment)
              return
            }
      }),
  )
}

/**
 * Helper to a single review summary in the conversation comments query store.
 * @param updateComment A function to update the review summary. The passed item is a **mutable** (`immer`) draft;
 * you should edit this and return void.
 */
export function setReviewSummaryQueryData(
  client: QueryClient,
  basePath: string,
  commentId: number,
  updateComment: (draft: WritableDraft<ConversationReviewSummary>) => void,
) {
  client.setQueryData(
    conversationCommentsQueryKey(basePath),
    data =>
      data &&
      produce(data, draftData => {
        for (const page of draftData.pages)
          for (const comment of page.data)
            // IDs are not unique across types, so it's important to also check `type`
            if (comment.type === 'review_summary' && comment.id === commentId) {
              updateComment(comment)
              return
            }
      }),
  )
}

/** Push a new item to the query data. */
export function pushConversationItemToQueryData(client: QueryClient, basePath: string, comment: ConversationComment) {
  // NOTE: When we add better paging support (not just loading everything up front), we will need to ensure that a
  // subsequent page load does not duplicate this item. We'll also need to ensure that everything in beween the other
  // items and this item is loaded, which may require loading all pages. If we add API-level sort support along with
  // paging, we'll need to make sure it's not possible to get a gap in the data between the most recent loaded comments
  // and the newly submitted comment (or at least indicate that gap to the user somehow).
  client.setQueryData(
    conversationCommentsQueryKey(basePath),
    data =>
      data &&
      produce(data, draftData => {
        // For now, we'll just push it to the last page since we know all data was loaded eagerly.
        draftData.pages.at(-1)?.data.push(comment)
      }),
  )
}
